import type { Message, MessageByType, MessageType } from "@textclub/common/";

type MessageSubscription = {
  type: MessageType;
  conversationId: string;
  resolve: (message: Message) => void;
};

export class AsyncMessagingHelper {
  private subscribers: MessageSubscription[] = [];

  /**
   * Wait for the next message of a given type in a particular conversation
   */
  waitForNextMessage<T extends MessageType>(
    type: T,
    conversationId: string,
    timeoutMs: number,
  ): Promise<MessageByType<T>> {
    const subscription: MessageSubscription = {
      type,
      conversationId,
      resolve: (_: Message) => {},
    };

    const promise = new Promise<MessageByType<T>>((resolve, reject) => {
      const timeout = setTimeout(() => {
        // Remove from set of subscribers
        this.subscribers = this.subscribers.filter((s) => s !== subscription);

        reject(
          new Error(
            `Internal error. Message with type '${type}' for conversationId '${conversationId}' not received before timeout of ${timeoutMs}ms`,
          ),
        );
      }, timeoutMs);

      subscription.resolve = (message: Message) => {
        clearTimeout(timeout);
        resolve(message as MessageByType<T>);
      };
    });

    this.subscribers.push(subscription);

    return promise;
  }

  /**
   * Resolve any promises created from waitForNextMessage(...) that match this message
   */
  resolveWaiting(message: Message): boolean {
    const remaining: MessageSubscription[] = [];

    for (const subscription of this.subscribers) {
      if (
        subscription.type === message.type &&
        subscription.conversationId === message.conversationId
      ) {
        subscription.resolve(message);
      } else {
        remaining.push(subscription);
      }
    }

    const resolved = this.subscribers.length - remaining.length;
    this.subscribers = remaining;

    return resolved > 0;
  }
}
